import { colors } from '@/styles/abstracts/colorVariable';
import { ArcElement, Chart as ChartJS, DoughnutController, Legend, Tooltip } from 'chart.js';
import cx from 'classnames';
import { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import Score, { ScoreProps } from '../Score/Score';
import styles from './DonutChart.module.css';

ChartJS.register(ArcElement, Tooltip, Legend, DoughnutController);

export const SCORE_RANGE_LIST = [100, 80, 70, 60, 50, 35, 25, 12, 5];

export const CHART_COLOR_SET: Record<(typeof SCORE_RANGE_LIST)[number], string> = {
  100: '#00B458',
  80: '#66C23A',
  70: '#A2CA27',
  60: '#D6D118',
  50: '#FFD60C',
  35: '#F5AC13',
  25: '#EA7E1C',
  12: '#DE4A25',
  5: '#CC0033',
};

const CHART_DEFAULT_COLOR = colors.grey[300];
const CHART_GREEN_COLOR = '#00b458';
const CHART_ORANGE_COLOR = '#ea7e1c';
const CHART_RED_COLOR = '#cc0033';

const MULTI_DONUT_COLOR_SET = [CHART_GREEN_COLOR, CHART_ORANGE_COLOR, CHART_RED_COLOR];
const NO_VALUE_COLOR_SET = [CHART_DEFAULT_COLOR];

export type DonutChartProps = ScoreProps & {
  'valueSet'?: Array<number>;
  'maxValue': number;
  'className'?: string;
  'background'?: string;
  'data-testid'?: string;
  'scoreTestId'?: string;
};

export default function DonutChart({
  value,
  valueSet,
  maxValue,
  size = 'M',
  className,
  background,
  'data-testid': dataTestId = 'doughnut-chart',
  scoreTestId,
  ...props
}: DonutChartProps) {
  const valueSetHasData = useMemo(() => {
    return valueSet ? !valueSet.every(score => score === 0) : false;
  }, [valueSet]);

  const data = useMemo(() => {
    if (!value && !valueSetHasData) return [1];
    if (valueSet && valueSetHasData) return [...valueSet];
    if (value) return [Math.abs(value), maxValue - Math.abs(value)];
    return [1];
  }, [maxValue, value, valueSet, valueSetHasData]);

  const backgroundColor = useMemo(() => {
    if (!value && !valueSetHasData) return NO_VALUE_COLOR_SET;
    if (valueSetHasData) return MULTI_DONUT_COLOR_SET;
    const valueColor = SCORE_RANGE_LIST.reduce((acc, score) => {
      if ((value / maxValue) * 100 <= Number(score)) return CHART_COLOR_SET[score];
      else return acc;
    }, '');
    return [valueColor, CHART_DEFAULT_COLOR];
  }, [maxValue, value, valueSetHasData]);

  const donutData = useMemo(() => {
    return {
      datasets: [
        {
          data: data || [1],
          backgroundColor: background ?? (backgroundColor || [CHART_DEFAULT_COLOR]),
        },
      ],
    };
  }, [data, backgroundColor, background]);

  const donutValueAttribute = useMemo(
    () => (valueSetHasData ? JSON.stringify(valueSet) : value?.toString()),
    [valueSet, value, valueSetHasData],
  );

  return (
    <div className={cx(styles.donutChartContainer, className)}>
      <section
        className={styles.donut}
        data-testid={dataTestId}
        data-size={size}
        data-chart-value={donutValueAttribute}
      >
        <Doughnut
          data={donutData}
          options={{
            plugins: { tooltip: { enabled: false } },
            maintainAspectRatio: true,
            events: [],
          }}
        />
      </section>
      <Score {...props} value={value} size={size} data-testid={scoreTestId} />
    </div>
  );
}
